import { useRef, useEffect, useState } from "react";
import classNames from "classnames/bind";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { CloseOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/CloseOutlined";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { useGalleryGesture } from "../hooks/useGalleryGesture";
import { GalleryObject, ImageClickPosition } from "../utils/utils.ts";
import styles from "./SuperZoomImage.module.scss";

const cx = classNames.bind(styles);

interface SuperZoomImageProps {
  mediaItems: GalleryObject[];
  selectedIndex: number;
  imageClickPosition: ImageClickPosition;
  isOpen: boolean;
  onClose: () => void;
  isDesktop: boolean;
}

export const SuperZoomImage = ({
  mediaItems,
  selectedIndex,
  imageClickPosition,
  isOpen,
  onClose,
  isDesktop,
}: SuperZoomImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [drag, resetAdjustments] = useGalleryGesture(imageRef);
  const [isMouseOver, setIsMouseOver] = useState(false);

  useEffect(() => {
    resetAdjustments(imageClickPosition);
  }, [selectedIndex, imageClickPosition]);

  const mediaItem = mediaItems[selectedIndex];
  if (!mediaItem) {
    return null;
  }

  const srcSetValues = [
    "$rsp-pdp-port-320$ 320w",
    "$rsp-pdp-port-640$ 640w",
    "$rsp-pdp-port-1080$ 1080w",
    "$rsp-pdp-port-1440$ 1440w",
  ];

  const generateSrcSetUrl = (rspValue: string) =>
    formatAmplienceImageURL(`${mediaItem.imageUrl}?${rspValue}`);

  const handleMouseMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") {
      setIsMouseOver(false);
      return;
    }
    setIsMouseOver(true);

    if (!containerRef.current || !imageRef.current) {
      return;
    }

    event.preventDefault();

    const {
      nativeEvent: { clientX, clientY },
    } = event;

    const containerBoundingClient =
      containerRef.current.getBoundingClientRect();
    const imageBoundingClient = imageRef.current.getBoundingClientRect();

    const offsetLeft = containerBoundingClient.left;
    const relativeMousePositionX = clientX - offsetLeft;
    const containerWidth = containerBoundingClient.width;
    const imageWidth = imageBoundingClient.width;
    const imageLeft =
      (relativeMousePositionX / containerWidth) * (imageWidth - containerWidth);

    const offsetTop = containerBoundingClient.top;
    const relativeMousePositionY = clientY - offsetTop;
    const containerHeight = containerBoundingClient.height;
    const imageHeight = imageBoundingClient.height;
    const imageTop =
      (relativeMousePositionY / containerHeight) *
      (imageHeight - containerHeight);

    imageRef.current.style.transform = `translate(-${imageLeft}px, -${imageTop}px)`;
  };

  const handleOnClick = () => {
    onClose();
  };

  return (
    <div
      ref={containerRef}
      className={cx(styles.imageWrapper, {
        hidden: !isOpen,
      })}
      aria-label={`Product image ${selectedIndex + 1}, ${mediaItem.title}`}
      data-testid={`gallery:super:zoom:image`}
      onPointerOver={handleMouseMove}
      onPointerMove={handleMouseMove}
      onClick={handleOnClick}
    >
      <img
        ref={imageRef}
        src={formatAmplienceImageURL(mediaItem.imageUrl)}
        srcSet={`${srcSetValues.map((v) => generateSrcSetUrl(v)).join(",")}`}
        sizes="100vw"
        loading="eager"
        className={styles.image}
        alt={`Zoomed in product image ${mediaItem.title}`}
        style={{
          left: isMouseOver ? 0 : drag[0],
          top: isMouseOver ? 0 : drag[1],
        }}
      />
      <IconButton
        icon={CloseOutlined}
        aria-label={"Close button"}
        data-testid="gallery:super:zoom:image:close:button"
        className={styles.closeButton}
        onClick={handleOnClick}
        background="filled"
        size={isDesktop ? "medium" : "small"}
      />
    </div>
  );
};
